/**
 * Arcade car dynamics, shared by the player's vehicle and by traffic.
 *
 * ADR-0011 chose this over a rigid-body model, and the deciding argument was
 * reuse: traffic needs eight to twenty vehicles moving at once, which a
 * physics body per car cannot afford. So the integrator lives here on its own,
 * with no notion of input devices, cameras or meshes — the player controller
 * feeds it a throttle from the keyboard and the traffic agents feed it one
 * from a path follower, and both get identical handling.
 *
 * The model in one line: speed is a scalar integrated along a heading, and
 * steering authority scales with speed so a car cannot pivot on the spot.
 * There is no suspension, no tyre model and no lateral slip.
 */

export interface CarSpec {
  /** Top speed forward, m/s. */
  maxSpeed: number;
  /** Top speed in reverse, m/s. Always well below forward. */
  maxReverse: number;
  /** Engine acceleration, m/s². */
  accel: number;
  /** Deceleration when the throttle opposes travel, m/s². */
  brake: number;
  /** Passive slowing with no input, m/s². */
  drag: number;
  /** Radians per second of heading change at the speed where steering peaks. */
  turnRate: number;
  /**
   * Speed, m/s, at which steering authority is full.
   *
   * Below it the car turns proportionally less, which is what stops a
   * stationary car spinning in place when the wheel is turned. Above it
   * authority tapers, so a car at top speed does not corner like a go-kart.
   */
  steerReference: number;
  /** Half-length and half-width, for the collision box. */
  halfLength: number;
  halfWidth: number;
}

export const PLAYER_CAR: CarSpec = {
  // 16 m/s is a deliberate cap, not a limitation - see ADR-0011. It is under
  // twice a sprint: fast enough to be worth taking, slow enough that a 200 m
  // world does not shrink to nothing.
  maxSpeed: 16,
  maxReverse: 5.5,
  accel: 6.2,
  brake: 14,
  drag: 3.4,
  turnRate: 1.75,
  steerReference: 7,
  halfLength: 2.35,
  halfWidth: 1.05,
};

export const TRAFFIC_CAR: CarSpec = {
  maxSpeed: 11,
  maxReverse: 0,
  accel: 4.4,
  brake: 9,
  drag: 2.6,
  turnRate: 2.1,
  steerReference: 6,
  halfLength: 2.2,
  halfWidth: 1.0,
};

/** Everything that changes frame to frame. Plain data, so it is easy to save. */
export interface CarState {
  x: number;
  z: number;
  /** Radians. 0 points along +Z, matching the rest of the world's convention. */
  heading: number;
  /** Signed: negative is reverse. */
  speed: number;
  /** -1..1, eased toward the requested steer so the wheel is not instant. */
  steer: number;
}

export interface CarInput {
  /** -1 (full reverse or brake) .. 1 (full throttle). */
  throttle: number;
  /** -1 (left) .. 1 (right). */
  steer: number;
  /** Strong deceleration regardless of travel direction. */
  handbrake: boolean;
}

export function createCarState(x: number, z: number, heading: number): CarState {
  return { x, z, heading, speed: 0, steer: 0 };
}

/**
 * Advance a car by `dt` seconds.
 *
 * Returns the position it *wants* to be at. Collision is the caller's problem:
 * the player controller resolves against the world's colliders and traffic
 * resolves against the car in front, and neither wants the other's rules baked
 * in here.
 */
export function stepCar(
  car: CarState,
  input: CarInput,
  spec: CarSpec,
  dt: number,
): { x: number; z: number } {
  // The steering wheel has weight. Snapping `steer` to the input makes the car
  // feel like it is on rails at low speed and twitchy at high speed; easing it
  // is most of what separates "responsive" from "nervous".
  const steerRate = 6.5;
  car.steer += (input.steer - car.steer) * Math.min(1, steerRate * dt);

  const moving = Math.abs(car.speed);
  // Braking rather than accelerating whenever the throttle opposes travel.
  // Without this, tapping reverse at speed launches the car backwards through
  // its own momentum instead of slowing it.
  const opposing = input.throttle !== 0 && Math.sign(input.throttle) !== Math.sign(car.speed);
  if (input.handbrake) {
    car.speed -= Math.sign(car.speed) * Math.min(moving, spec.brake * 1.35 * dt);
  } else if (input.throttle === 0) {
    car.speed -= Math.sign(car.speed) * Math.min(moving, spec.drag * dt);
  } else if (opposing && moving > 0.4) {
    car.speed -= Math.sign(car.speed) * Math.min(moving, spec.brake * dt);
  } else {
    car.speed += input.throttle * spec.accel * dt;
  }
  car.speed = clamp(car.speed, -spec.maxReverse, spec.maxSpeed);
  if (Math.abs(car.speed) < 0.04) car.speed = 0;

  // Steering authority: zero at rest, full at steerReference, tapering above.
  // The taper is what stops a car at top speed cornering like a go-kart.
  const s = Math.abs(car.speed);
  const authority = s < spec.steerReference
    ? s / spec.steerReference
    : spec.steerReference / s;
  // In reverse the front wheels still point the same way, so the body swings
  // the other way. Omitting this makes reversing into a parking space feel
  // inverted in exactly the way real drivers expect it not to be.
  const direction = car.speed < 0 ? -1 : 1;
  car.heading += car.steer * spec.turnRate * authority * direction * dt;

  return {
    x: car.x + Math.sin(car.heading) * car.speed * dt,
    z: car.z + Math.cos(car.heading) * car.speed * dt,
  };
}

/**
 * The four corners of a car, in world space.
 *
 * Used for collision: an axis-aligned box around a rotated car is far too
 * generous at 45°, and a car that stops a metre before the wall it is
 * approaching diagonally reads as broken.
 */
export function carCorners(
  car: Pick<CarState, 'x' | 'z' | 'heading'>,
  spec: CarSpec,
): [number, number][] {
  const c = Math.cos(car.heading);
  const s = Math.sin(car.heading);
  const out: [number, number][] = [];
  for (const [l, w] of [
    [spec.halfLength, spec.halfWidth],
    [spec.halfLength, -spec.halfWidth],
    [-spec.halfLength, spec.halfWidth],
    [-spec.halfLength, -spec.halfWidth],
  ] as [number, number][]) {
    out.push([car.x + s * l + c * w, car.z + c * l - s * w]);
  }
  return out;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Shortest signed angle from `from` to `to`, in radians. */
export function angleDelta(from: number, to: number): number {
  let d = (to - from) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}
